<script lang="ts" setup>
import Konva from "konva";
import { computedWithControl } from "@vueuse/core";
import Image = Konva.Image;
import Stage = Konva.Stage;
import Layer = Konva.Layer;
import Transformer = Konva.Transformer;
import { StorageGet, StorageSet } from "@/tools/localStorage";
import { useRoute } from "vue-router";
import i18n from "@/locales";

const route = useRoute();

const props = withDefaults(
  defineProps<{
    stage: Stage;
    layer: Layer;
    shapes: Image[];
    activeShapes: Image[];
    transformer: Transformer;
    title?: string;
  }>(),
  {
    title: i18n.global.t("Texts.canvas"),
  }
);
const emit = defineEmits<{
  (e: "update:shapes", params: Image[]): void;
  (e: "update:activeShapes", params: Image[]): void;
}>();

const sortedShapes = computedWithControl(
  () => props.shapes,
  () => {
    return [...props.shapes].sort((a, b) => b.getZIndex() - a.getZIndex());
  }
);

const isAnimationNode = (imgNode: Image) => {
  return imgNode.image() instanceof HTMLCanvasElement;
};
const getUrlFromImageNode = (imgNode: Image) => {
  return isAnimationNode(imgNode) ? imgNode.attrs.url : imgNode.attrs.image.src;
};

const onItemClick = (item: Image) => {
  emit("update:activeShapes", [item]);
};
const onLockActive = () => {
  const lock = !props.activeShapes[0].attrs.lock;
  props.activeShapes.forEach((s) => onLockItem(s, lock));
};
const onLockItem = (item: Image, lock?: boolean) => {
  const status = lock ?? !item.attrs.lock;
  item.attrs.lock = status;
  item.setDraggable(!status);
  if (status) {
    emit("update:activeShapes", []);
  }
};
const onDelete = (item: Image) => {
  emit(
    "update:shapes",
    props.shapes.filter((s) => s !== item)
  );
  emit(
    "update:activeShapes",
    props.activeShapes.filter((s) => s !== item)
  );
  item.destroy();
};
const onMoveUp = (image: Image) => {
  image.moveUp();
  sortedShapes.trigger();
};
const onMoveDown = (image: Image) => {
  image.moveDown();
  sortedShapes.trigger();
};
const onHorizontalFlip = () => {
  props.activeShapes.forEach((s) => {
    s.to({ scaleX: -s.scaleX() });
  });
};
</script>
<template>
  <div
    class="rounded-tr-lg border-t border-r border-primary grid grid-rows-[auto_1fr]"
  >
    <div
      class="px-2 py-1 border-b border-primary flex justify-between items-center"
    >
      <span class="font-bold">{{ title }}</span>
      <div class="flex gap-1">
        <button
          :disabled="activeShapes.length === 0"
          :title="$t('Buttons.lockOrUnlock')"
          class="btn btn-xs btn-ghost rounded"
          @click="onLockActive()"
        >
          <lock-icon class="icon-sm" />
        </button>
        <button
          :disabled="activeShapes.length === 0"
          :title="$t('Buttons.horizontalFlip')"
          class="btn btn-xs btn-ghost rounded"
          @click="onHorizontalFlip"
        >
          <i class="iconfont icon-jingxiang md:text-sm" />
        </button>
        <add-image-to-stage
          v-if="stage && layer"
          :imageShapes="shapes"
          :layer="layer"
          :media-select-opt="{
            enablePortraitGallery: true,
            multiple: true,
            maxCount: 10,
          }"
          :stage="stage"
          @update:image-shapes="emit('update:shapes', $event)"
        >
          <button
            :title="$t('Buttons.addImage')"
            class="btn btn-xs btn-ghost rounded"
          >
            <pic-plus class="icon-2xl" />
          </button>
        </add-image-to-stage>
      </div>
    </div>
    <div
      v-if="shapes.length === 0"
      class="flex items-center justify-center text-base-content/50"
    >
      {{ $t("Texts.clickUpperRight") }}
    </div>
    <div v-else class="h-full overflow-y-auto">
      <div
        v-for="(item, idx) in sortedShapes"
        :key="item.id"
        :class="{
          'bg-blue-300/50': activeShapes.find((s) => s == item),
        }"
        class="flex py-2 px-4 items-center justify-between border-b cursor-pointer"
        @click="onItemClick(item)"
      >
        <div class="flex items-center gap-2">
          <img
            :src="getUrlFromImageNode(item)"
            alt=""
            class="h-10"
            crossorigin="anonymous"
          />
          <div v-if="isAnimationNode(item)" class="badge badge-sm">
            {{ $t("Texts.animation") }}
          </div>
        </div>
        <div class="flex gap-1">
          <button
            v-show="idx !== 0 && !item.attrs.lock"
            class="btn btn-xs btn-ghost"
            @click.stop="onMoveUp(item)"
          >
            <i class="iconfont icon-up text-xs" />
          </button>
          <button
            v-show="idx !== sortedShapes.length - 1 && !item.attrs.lock"
            class="btn btn-xs btn-ghost"
            @click.stop="onMoveDown(item)"
          >
            <i class="iconfont icon-down text-xs" />
          </button>
          <button
            v-show="!item.attrs.lock"
            class="btn btn-xs btn-ghost"
            @click.stop="onDelete(item)"
          >
            <delete-icon class="icon-sm" />
          </button>
          <button
            v-if="item.attrs.lock"
            class="btn btn-xs btn-ghost"
            @click.stop="onLockItem(item)"
          >
            <lock-icon class="icon-sm text-primary" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
